import { useIsOrgReadOnly } from '@/components/auth/claimsUtils';
import RequirePermissions from '@/components/auth/requirePermissions';
import { EnhancedFormikForm } from '@/components/EnhancedFormikForm';
import { MainContainer } from '@/components/MainContainer';
import { calculateEnergyBackupSystemDesignTotalCost } from '@/components/Quotes/EnergyBackup.utils';
import { QuoteDrawer } from '@/components/Quotes/QuoteDrawer';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import { useGetAvailableEquipmentQuery } from '@/services/availableEquipment';
import { useGetContractsQuery, useVoidContractMutation } from '@/services/contract';
import { isErrorWithData } from '@/services/helpers';
import { useFetchEnergyBackupAccountPricingMutation } from '@/services/pricing';
import {
  useCreateSystemDesignMutation,
  useGetCurrentDesignQuery,
  useLazyGetCurrentDesignQuery,
} from '@/services/systemDesign';
import { Alert, Box, Button, Modal, Spinner, toast, useOpenClose } from '@palmetto/palmetto-components';
import { FieldArray, Formik } from 'formik';
import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  Clientize,
  ContractStatus,
  EnergyBackupProductPricingResponse,
  EnergyBackupSystem,
  EquipmentType,
  EnergyBackupSystemDesign as IEnergyBackupSystemDesign,
  ProgramType,
  Quote,
  QuoteStatus,
  UserPermissions,
} from 'types';

import { useGetAccountQuery } from '../../../services/accounts';
import { useCreateQuoteMutation, useGetQuotesQuery, useVoidQuoteMutation } from '../../../services/quotes';

import { EquipmentOption } from './EquipmentOption';

/** Client-side representation of energy backup pricing from the API */
type EnergyBackupPricingOption = Clientize<EnergyBackupProductPricingResponse>;

/** Client-side representation of energy backup quote from the API */
type EnergyBackupQuote = Quote<ProgramType.energyBackup>;

/** Aggregated equipment model for display purposes */
interface AggregatedEquipmentModel {
  name: string;
  quantity: number;
  capacity: number;
  productId?: string;
}

/** Aggregated equipment item by type for the System Design view */
interface AggregatedEquipment {
  type: EquipmentType;
  manufacturer: string;
  totalCost: number;
  models: AggregatedEquipmentModel[];
}

/**
 * Aggregates equipment items by type for display on the System Design page.
 * When equipment has been split into separate items (e.g., for different SKUs on Install Package),
 * this function combines them back for the System Design view where we show by equipment type.
 *
 * Example:
 * Input: [
 *   { type: "backupBattery", models: [{ name: "Powerwall 3", quantity: 1 }], totalCost: 27000 },
 *   { type: "backupBattery", models: [{ name: "Powerwall 3", quantity: 1 }], totalCost: 0 }
 * ]
 * Output: [
 *   { type: "backupBattery", models: [{ name: "Powerwall 3", quantity: 2 }], totalCost: 27000 }
 * ]
 */
const aggregateEquipmentByType = (equipment: EnergyBackupSystem[]): AggregatedEquipment[] => {
  if (!equipment?.length) return [];

  const groupedByType = new Map<EquipmentType, AggregatedEquipment>();

  for (const item of equipment) {
    const type = item.type;
    if (!groupedByType.has(type)) {
      // First item of this type - clone it as the base
      groupedByType.set(type, {
        type: item.type,
        manufacturer: item.manufacturer,
        totalCost: item.totalCost || 0,
        models: [],
      });
    } else {
      // Add to totalCost for subsequent items
      const existing = groupedByType.get(type);
      if (existing) {
        existing.totalCost += item.totalCost || 0;
      }
    }

    const aggregated = groupedByType.get(type);
    if (!aggregated) continue;

    // Merge models - combine quantities for models with the same name
    for (const model of item.models || []) {
      const existingModel = aggregated.models.find((m) => m.name === model.name);
      if (existingModel) {
        existingModel.quantity += model.quantity || 1;
      } else {
        aggregated.models.push({
          name: model.name,
          quantity: model.quantity || 1,
          capacity: model.capacity,
          productId: model.productId,
        });
      }
    }
  }

  return Array.from(groupedByType.values());
};

interface CreateEnergyBackupSystemValues {
  equipment: {
    type: EquipmentType;
    manufacturer: { label: string; value: string };
    totalCost: number;
    models: {
      quantity: { label: string; value: number };
      capacity: number;
      name: { label: string; value: string };
      productId: string;
    }[];
  }[];
}

export const EnergyBackupSystemDesign = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<CreateEnergyBackupSystemValues | undefined>(undefined);
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const allowsAction = useIsOrgReadOnly();
  const [voidContract, { isLoading: isVoidingActiveContract }] = useVoidContractMutation({
    fixedCacheKey: 'useVoidContractMutationKey',
  });
  const [voidQuote, { isLoading: isVoidingQuote }] = useVoidQuoteMutation({
    fixedCacheKey: 'useVoidQuoteMutationKey',
  });
  const targetRef = useRef(null);

  const { isOpen: isDrawerOpen, handleOpen: openDrawer, handleClose: closeDrawer } = useOpenClose();
  const [getCurrentSystemDesign, { data: createdSystemDesign }] = useLazyGetCurrentDesignQuery();
  const [createQuote] = useCreateQuoteMutation();

  const [
    fetchPricing,
    { data: pricingData = [] as EnergyBackupPricingOption[], isLoading: isPricingLoading, status: pricingStatus },
  ] = useFetchEnergyBackupAccountPricingMutation();
  const { data: systemDesign } = useGetCurrentDesignQuery(
    {
      id,
      programType: ProgramType.energyBackup,
    },
    { skip: !id },
  ) as { data: IEnergyBackupSystemDesign | undefined };
  const parentStyles = useParentDivLayoutStyles(targetRef);
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isDrawerOpen);

  const { data: availableEquipment, isLoading: isAvailableEquipmentLoading } = useGetAvailableEquipmentQuery(
    { accountId: id },
    {
      skip: !id,
    },
  );
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const programType = account?.programType;
  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType },
  );
  const [createSystemDesign, { isLoading: isCreatingSystemDesign }] = useCreateSystemDesignMutation();
  const isLoading = isAccountLoading || isQuotesLoading || isAvailableEquipmentLoading;

  const activeQuote = (quotes as EnergyBackupQuote[] | undefined)?.find(
    (quote) => quote.status === QuoteStatus.active,
  );
  const { data: contracts } = useGetContractsQuery(id);

  const activeContract = useMemo(() => {
    return contracts?.find(
      (contract) => ![ContractStatus.voided, ContractStatus.rejected].includes(contract.status),
    );
  }, [contracts]);

  const systemDesignCost = useMemo(() => {
    if (!createdSystemDesign && !systemDesign) {
      return 0;
    }
    return calculateEnergyBackupSystemDesignTotalCost(createdSystemDesign || systemDesign);
  }, [createdSystemDesign, systemDesign]);

  const toggleDrawer = () => {
    if (isDrawerOpen) {
      closeDrawer();
    } else {
      openDrawer();
    }
  };

  /**
   * @description Voids a quote (and an active contract, if any)
   * and generates a new system design.
   */
  const handleConfirmModalSubmit = async () => {
    closeModal();
    if (formValues && activeQuote) {
      try {
        if (activeContract) {
          await voidContract({
            accountId: String(id),
            contractId: String(activeContract.id),
          }).unwrap();
          toast.success('Active Contract voided successfully');
        }
        await voidQuote({ accountId: id, quoteId: activeQuote.id }).unwrap();
        toast.success('Quote voided successfully');
        const data = {
          equipment: formValues.equipment
            .filter((e) => !!e)
            .map((e) => ({
              type: e.type,
              manufacturer: e.manufacturer.value,
              totalCost: e.totalCost,
              models: e.models.map((m) => ({
                name: m.name.value,
                quantity: m.quantity.value,
                capacity: m.capacity,
                productId: m.productId,
              })),
            })),
        };
        await createSystemDesign({
          accountId: account?.id,
          systemDesign: data,
          programType: programType as ProgramType,
        }).unwrap();
        toast.success('System design saved successfully');
        getCurrentSystemDesign({
          id,
          programType: ProgramType.energyBackup,
        });
        await fetchPricing({
          accountId: id,
          useCurrentSystemDesign: true,
        });
        openDrawer();
      } catch (e) {
        if (isErrorWithData(e)) {
          toast.error(e.data?.message || 'Error saving system design');
        } else {
          toast.error('Error saving system design');
        }
      }
    }
  };

  const isVoiding = isVoidingQuote || isVoidingActiveContract;

  const handleSubmit = async (values: CreateEnergyBackupSystemValues) => {
    setFormValues(values);
    const totalSystemDesignCost = calculateEnergyBackupSystemDesignTotalCost(
      values as any as IEnergyBackupSystemDesign,
    );
    if (activeQuote && totalSystemDesignCost !== activeQuote.totalSystemCost) {
      openModal();
      return;
    }
    const data = {
      equipment: values.equipment
        .filter((e) => !!e)
        .map((e) => ({
          type: e.type,
          manufacturer: e.manufacturer.value,
          totalCost: e.totalCost,
          models: e.models.map((m) => ({
            name: m.name.value,
            quantity: m.quantity.value,
            capacity: m.capacity,
            productId: m.productId,
          })),
        })),
    };
    try {
      await createSystemDesign({
        accountId: account?.id,
        systemDesign: data,
        programType: programType as ProgramType,
      }).unwrap();
      toast.success('Created system design');
      await getCurrentSystemDesign({
        id,
        programType: ProgramType.energyBackup,
      });
      await fetchPricing({
        accountId: account?.id,
        useCurrentSystemDesign: true,
      }).unwrap();
      openDrawer();
    } catch (e) {
      if (e instanceof yup.ValidationError) {
        toast.error(e?.message || 'Error saving account system design');
      } else if (isErrorWithData(e)) {
        toast.error(e?.data?.message || 'Error saving account system design');
      } else {
        console.error(e);
        toast.error('Error saving account system design');
      }
    }
  };

  const ratesAndTerms = useMemo(() => {
    const typedPricingData = pricingData as EnergyBackupPricingOption[];
    if (!typedPricingData || !typedPricingData.length) {
      return { rates: [], terms: [] };
    }

    const uniqueRates: number[] = [...new Set(typedPricingData.map((item) => item.escalationRate))];
    const rates = uniqueRates
      .toSorted((a, b) => a - b)
      .map((rate) => ({
        label: `${(rate * 100).toFixed(2)}%`,
        value: rate.toString(),
        id: rate.toString(),
      }));

    const uniqueTerms: number[] = [...new Set(typedPricingData.map((item) => item.term))];
    const terms = uniqueTerms
      .toSorted((a, b) => a - b)
      .map((term) => ({
        label: `${term} years`,
        value: term.toString(),
        id: term.toString(),
      }));

    return { rates, terms };
  }, [pricingData]);

  const handleCreateQuote = async (values: any) => {
    const creationPayload = {
      quoteData: {
        productId: values?.productId,
        externalReference: account?.externalReference || undefined,
        totalFinancedAmount: systemDesignCost,
      },
      id: account.id,
      programType: ProgramType.energyBackup,
    } as any;
    try {
      await createQuote(creationPayload).unwrap();
      navigate(`/accounts/${id}`);
      toast.success('Quote created successfully');
    } catch (e) {
      toast.error('Error creating quote');
      console.error(e);
    }
  };

  const initialValues = useMemo(() => {
    if (!account?.systemDesign) {
      return {};
    }
    // Aggregate equipment items by type to handle cases where Install Package
    // has split them into separate items (e.g., for different SKUs)
    const aggregatedEquipment = aggregateEquipmentByType(account?.systemDesign?.equipment);
    return {
      equipment: aggregatedEquipment.map((e: any) => ({
        type: e.type,
        manufacturer: { label: e.manufacturer, value: e.manufacturer },
        models: e?.models?.map((m: any) => ({
          name: { label: m.name, value: m.name },
          quantity: { label: m.quantity.toString(), value: m.quantity },
          capacity: m.capacity,
          productId: m.productId,
        })) ?? [{ name: { label: '', value: '' }, capacity: 0, quantity: { label: '1', value: 1 }, productId: '' }],
        totalCost: e.totalCost,
      })),
    };
  }, [account]);

  return (
    <MainContainer>
      {isLoading ? (
        <Box display="block" textAlign="center" padding="lg" childGap="xl">
          <Spinner size="lg" />
        </Box>
      ) : (
        <Box
          overflow="hidden"
          childGap="lg"
          ref={targetRef}
          style={{
            marginRight: drawerMarginStyles,
            position: 'relative',
            transition: 'margin-right 0.3s ease',
          }}
        >
          <Formik
            initialValues={{
              equipment: [
                {
                  type:
                    availableEquipment?.availableEquipment?.length === 1
                      ? availableEquipment?.availableEquipment?.[0].equipmentType
                      : '',
                  manufacturer: { label: '', value: '' },
                  totalCost: 0,
                  models: [
                    {
                      name: { label: '', value: '' },
                      quantity: { label: '1', value: 1 },
                      productId: '',
                      capacity: 0,
                    },
                  ],
                },
              ],
              ...initialValues,
            }}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            validateOnChange={false}
          >
            {({ isSubmitting, values }) => {
              const totalCost = values?.equipment?.reduce(
                (sum: number, item: { totalCost: number }) => sum + (item.totalCost || 0),
                0,
              );
              return (
                <EnhancedFormikForm noValidate style={{ width: '100%' }}>
                  {!availableEquipment?.availableEquipment?.length && (
                    <Alert
                      variant="warning"
                      hasIcon
                      title="No available equipment"
                      message="There is no equipment available for this account."
                    />
                  )}
                  <Box gap="lg" margin="lg 0 0 0">
                    <FieldArray
                      name="equipment"
                      render={() =>
                        availableEquipment?.availableEquipment?.map(
                          (
                            equipment: {
                              equipmentType: EquipmentType;
                              priceCaps: { manufacturer: string; maxPrice: number; maxTerm: number }[];
                            },
                            index: number,
                          ) => (
                            <EquipmentOption
                              key={equipment.equipmentType}
                              equipmentType={equipment.equipmentType}
                              index={index}
                              hasActiveQuote={Boolean(activeQuote)}
                              availableEquipmentItems={availableEquipment?.availableEquipment?.length}
                            />
                          ),
                        )
                      }
                    />
                  </Box>
                  <Box alignItems="flex-end" margin="md" padding={{ base: 'lg 0 0 0' }} gap="xs">
                    <Box direction="row" gap="xs" alignItems="center">
                      <Box fontWeight="medium" fontSize="md">
                        Total System Design Cost:
                      </Box>
                      <Box fontWeight="bold" fontSize="lg">
                        {currencyFormatter.format(totalCost)}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    direction={{
                      base: 'column',
                      tablet: 'row',
                    }}
                    childGap="sm"
                    gap="lg"
                    style={{ flexShrink: 0 }}
                    padding={{ base: 'lg' }}
                    background="primary"
                    wrap={true}
                  >
                    {activeQuote ? (
                      <Box alignSelf="flex-start" direction="row" gap="lg">
                        <Box gap="xs">
                          <Box fontSize="xl" fontWeight="bold">
                            {currencyFormatter.format(activeQuote.totalSystemCost)}
                          </Box>
                          <Box fontSize="md">Total Financed Amount</Box>
                        </Box>
                        <Box gap="xs">
                          <Box direction="row" alignItems="center" gap="2xs">
                            <Box fontSize="xl" fontWeight="bold">
                              {currencyFormatter.format(
                                activeQuote.systemPricingDetails.find((d: any) => d.year === 1)?.monthlyPayment || 0,
                              )}
                            </Box>
                            <Box fontSize="md">/mo</Box>
                          </Box>
                          <Box fontSize="md">Year 1 Monthly Payment</Box>
                        </Box>
                        <Box gap="xs">
                          <Box direction="row" alignItems="center" gap="2xs">
                            <Box fontSize="xl" fontWeight="bold">
                              {activeQuote.systemPricingDetails.length}
                            </Box>
                            <Box fontSize="md">{activeQuote.systemPricingDetails.length > 1 ? `years` : `year`}</Box>
                          </Box>
                          <Box fontSize="md">Customer Term Length</Box>
                        </Box>
                      </Box>
                    ) : null}
                    <RequirePermissions
                      permissions={[UserPermissions.admin, UserPermissions.editor]}
                      checkAllPermissions={false}
                    >
                      <Box style={{ marginLeft: 'auto' }} direction="row" gap="sm">
                        <Button
                          as="a"
                          href={`/accounts/${id ? id : ''}`}
                          variant="secondary"
                          tone="neutral"
                          size="md"
                          isDisabled={isSubmitting || isCreatingSystemDesign || isVoiding}
                          isLoading={isSubmitting || isCreatingSystemDesign || isVoiding}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="md"
                          variant="primary"
                          type="submit"
                          isDisabled={isSubmitting || isCreatingSystemDesign || isVoiding}
                          isLoading={isSubmitting || isCreatingSystemDesign || isVoiding}
                        >
                          Save & Get Pricing
                        </Button>
                      </Box>
                    </RequirePermissions>
                  </Box>
                </EnhancedFormikForm>
              );
            }}
          </Formik>
        </Box>
      )}
      {!pricingData.length && pricingStatus !== 'uninitialized' && !isPricingLoading ? (
        <Alert variant="info" message="No pricing options available for this system design" />
      ) : null}
      <QuoteDrawer
        isOpen={isDrawerOpen}
        toggleDrawer={toggleDrawer}
        ratesAndTerms={ratesAndTerms}
        handleCreateQuote={handleCreateQuote}
        isLoading={isPricingLoading || isCreatingSystemDesign || isVoiding}
        programType={ProgramType.energyBackup}
        systemDesignCost={systemDesignCost}
        isPricingLoading={isPricingLoading}
        pricingData={pricingData}
      />
      <Modal ariaLabelledBy="costMismatch" isOpen={isModalOpen} onDismiss={closeModal}>
        <Modal.Header id="costMismatch" title="Cost Mismatch" onDismiss={closeModal} />
        <Modal.Body>
          The total system design cost does not match the active quote cost. Would you like to void the existing
          quote {activeContract ? 'and active contract' : ''} and create a new one?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" tone="neutral" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmModalSubmit} disabled={!allowsAction}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </MainContainer>
  );
};
